import {
  L0ActualDate,
  L1ActualDate,
  L2ActualDate,
  L3ActualDate,
  L4ActualDate,
  L5ActualDate,
  SubmittedForL1PlanDate,
  SubmittedForL2PlanDate,
  SubmittedForL3PlanDate,
  SubmittedForL4PlanDate,
  SubmittedForL5PlanDate,
  L1PlanDate,
  L2PlanDate,
  L3PlanDate,
  L4PlanDate,
  L5PlanDate,
  getCancellationDate,
  getQualitativeKPI,
  L1OriginalPlanDate,
  L2OriginalPlanDate,
  L3OriginalPlanDate,
  L4OriginalPlanDate,
  L5OriginalPlanDate,
  getSegmentalPBT
} from '@/utils/Sort/checkDate/getDate';
import LLatestActualDate from '@/utils/Sort/checkDate/LLatestActualDate';
import getEarliestMilestoneStartDate from '@/utils/Sort/checkDate/checkMilestoneEarliestStartDate';
import { getLatestMilestoneEndDate } from '@/utils/Sort/checkDate/checkMilestoneLatestEndDate';
import calculateNetRecurringBenefit from '@/utils/calculation/calculationRecurringBenefit';
import calculateOneTimeBenefit from '@/utils/calculation/calculateOneTimeBenefit';
import calculateOneTimeImplementationCost from '@/utils/calculation/calculateOneTimeImplementationCost';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import checkToFinanceApprovalStatus from '@/utils/Sort/checkTOFinanceApprovalStatus';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import getNetRecurringBenefitCategory from '@/utils/Sort/getNetRecurringBenefitCatagory';
import countKPIs from '@/utils/Sort/numberOfKPICount/KPICount';
import countValueLinkedKPIs from '@/utils/Sort/numberOfKPICount/valueLinkedKPICount';
import countHealthKPIs from '@/utils/Sort/numberOfKPICount/healthKPICount';
import countLeadingKPIs from '@/utils/Sort/numberOfKPICount/leadingKPICount';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import getImplementationCostCatagory from './getImplementationCostCatagory';
import determineStage from '@/utils/DetermineStage';
import { determinePlanningSummary } from '@/utils/planningSummary';
import sortInitiativesByActuals from './sortByActual';
import getNetRecurringBenefitChangeCategoryL4Plus from '../color/getNetRecurringBenefitCatagoryL4Plus';
import getNetRecurringBenefitChangeCategoryL3Plus from '../color/getNetRecurringBenefitCatagoryL3Plus';
import getNetRecurringBenefitChangeCategoryL2Plus from '../color/getNetRecurringBenefitCatagoryL2Plus';
import getNetRecurringBenefitChangeCategoryL1Plus from '../color/getNetRecurringBenefitCatagoryL1Plus';
import getNetRecurringBenefitChangeCategoryL0Plus from '../color/getNetRecurringBenefitCatagoryL0Plus';
import getComplianceStatus from './complianceCheck';
import getFrequencyAndDirection from '../color/getFrequencyAndDirection';
import getTypeOfImpact from '../color/getTypeOfImpact';
import getSubworkstreamCatagory from '../color/getSubworkstreamCatagory';
import planningSummarySimplified from '../planningSummarySimplified';
import getSimplifiedPlanningSummary from '@/components/Kanban/Workbook/getMilestonePlanningSummary';
import getSector from '../color/getSector';
import getSubsector from '../color/getSubsector';

const stringSort = (a, b, sortOrder, getVal) => {
  const valA = String(getVal(a) ?? '').toLowerCase();
  const valB = String(getVal(b) ?? '').toLowerCase();

  if (sortOrder === 'asc') {
    return valA.localeCompare(valB);
  }
  return valB.localeCompare(valA);
};

// Helper function to handle common numerical comparison logic
const numberSort = (a, b, sortOrder, getVal) => {
  const valA = getVal(a) || 0;
  const valB = getVal(b) || 0;
  if (sortOrder === 'asc') {
    return valA - valB;
  }
  return valB - valA;
};

const dateSort = (a, b, sortOrder, getVal) => {
  const normalizeToDate = (val) => {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val)) return val;
    const date = new Date(val);
    return isNaN(date) ? null : date;
  };
  const valA = normalizeToDate(getVal(a))?.getTime() ?? null;
  const valB = normalizeToDate(getVal(b))?.getTime() ?? null;
  if (valA === null && valB === null) return 0;
  if (valA === null) return 1; // a is null, so it goes after b
  if (valB === null) return -1; // b is null, so a goes before b

  return sortOrder === 'asc' ? valA - valB : valB - valA;
};

// Main initiative sort function
export function applyInitiativeSort(filtered, sortField, sortOrder, getNameToSort, subworkstream, subsectorData) {
  // Early return if filtered is not an array or empty
  if (!Array.isArray(filtered) || filtered.length === 0) {
    return filtered;
  }

  // If no sortField is provided, sort by initiativeNumber descending by default
  if (!sortField || sortField === 'None' || sortField === 'initiativeNumber') {
    const sortedCopy = [...filtered].sort((a, b) => {
      const aNum = parseInt(a.initiativeNumber) || 0;
      const bNum = parseInt(b.initiativeNumber) || 0;
      return bNum - aNum; // Descending order
    });
    filtered.length = 0;
    filtered.push(...sortedCopy);
    return filtered;
  }

  const effectiveSortOrder = sortOrder || 'asc';

  const sorter = (a, b) => {
    // Consolidated Person Name Sorting
    if (
      ['workstreamLeadInfo', 'workstreamSponsorInfo', 'initiativeOwnerInfo', 'initiativeOwnerBackupInfo', 'AllowOnlyTo'].includes(sortField)
    ) {
      const nameA = getNameToSort(a, sortField);
      const nameB = getNameToSort(b, sortField);
      const firstNameA = nameA?.firstName?.toLowerCase() || '';
      const firstNameB = nameB?.firstName?.toLowerCase() || '';
      if (firstNameA !== firstNameB) {
        return effectiveSortOrder === 'asc' ? firstNameA.localeCompare(firstNameB) : firstNameB.localeCompare(firstNameA);
      }
      const lastNameA = nameA?.lastName?.toLowerCase() || '';
      const lastNameB = nameB?.lastName?.toLowerCase() || '';
      return effectiveSortOrder === 'asc' ? lastNameA.localeCompare(lastNameB) : lastNameB.localeCompare(lastNameA);
    }

    // Consolidated Numerical Sorting
    if (['Number of Attachment', 'numberOfKpis', 'numberOfValueLinkedKpis', 'numberOfHealthKpis', 'numberOfLeadingIndicatorKpis', 'netRecurringBenefitsLatest', 'netRecurringBenefitsPlanned', 'implementationCostsPlanned', 'implementationCostsLatest', 'oneTimeBenefitsPlanned', 'oneTimeBenefitsLatest', 'initiativeNumber', 'initiativeNumber - initiativeName'].includes(sortField)) {
      const getVal = (item) => {
        switch (sortField) {
          case 'Number of Attachment':
            return item.attachmentCount;
          case 'numberOfKpis':
            return countKPIs(item);
          case 'numberOfValueLinkedKpis':
            return countValueLinkedKPIs(item);
          case 'numberOfHealthKpis':
            return countHealthKPIs(item);
          case 'numberOfLeadingIndicatorKpis':
            return countLeadingKPIs(item);
          case 'netRecurringBenefitsLatest':
          case 'netRecurringBenefitsPlanned':
            return calculateNetRecurringBenefit(item);
          case 'implementationCostsPlanned':
          case 'implementationCostsLatest':
            return calculateOneTimeImplementationCost(item);
          case 'oneTimeBenefitsPlanned':
          case 'oneTimeBenefitsLatest':
            return calculateOneTimeBenefit(item);
          case 'initiativeNumber':
            return parseInt(item.initiativeNumber) || 0;
          case 'initiativeNumber - initiativeName':
            return parseInt(item.initiativeNumber) || 0;
          default:
            return 0;
        }
      };
      return numberSort(a, b, effectiveSortOrder, getVal);
    }

    // Date Sorting
    if ([
      'l2ForecastDate', 'l3PlannedDate', 'l4PlannedDate', 'l5ForecastDate', 'l1PlannedDate', 'l0ActualDate', 'l1ActualDate', 'l2ActualDate', 'l3ActualDate', 'l4ActualDate', 'l5ActualDate',
      'subForL1ApprovDate', 'subForL2ApprovDate', 'subForL3ApprovDate', 'subForL4ApprovDate', 'subForL5ApprovDate',
      'latestActualDateAllLGates', 'latestMilestoneEndDate', 'earliestMilestoneStartDate', 'cancellationDate', 'L1 Original Planned Date',
      'L2 Original Planned Date', 'L3 Original Planned Date', 'L4 Original Planned Date', 'L5 Original Planned Date'
    ].includes(sortField)) {

      const getVal = (item) => {
        switch (sortField) {
          case 'l1PlannedDate':
            return L1PlanDate(item) || null;
          case 'l2ForecastDate':
            return L2PlanDate(item) || null;
          case 'l3PlannedDate':
            return L3PlanDate(item) || null;
          case 'l4PlannedDate':
            return L4PlanDate(item) || null;
          case 'l5ForecastDate':
            return L5PlanDate(item) || null;
          case 'L1 Original Planned Date':
            return L1OriginalPlanDate(item) || null;
          case 'L2 Original Planned Date':
            return L2OriginalPlanDate(item) || null;
          case 'L3 Original Planned Date':
            return L3OriginalPlanDate(item) || null;
          case 'L4 Original Planned Date':
            return L4OriginalPlanDate(item) || null;
          case 'L5 Original Planned Date':
            return L5OriginalPlanDate(item) || null;
          case 'l0ActualDate':
            return L0ActualDate(item) || null;
          case 'l1ActualDate':
            return L1ActualDate(item) || null;
          case 'l2ActualDate':
            return L2ActualDate(item) || null;
          case 'l3ActualDate':
            return L3ActualDate(item) || null;
          case 'l4ActualDate':
            return L4ActualDate(item) || null;
          case 'l5ActualDate':
            return L5ActualDate(item) || null;
          case 'subForL1ApprovDate':
            return SubmittedForL1PlanDate(item) || null;
          case 'subForL2ApprovDate':
            return SubmittedForL2PlanDate(item) || null;
          case 'subForL3ApprovDate':
            return SubmittedForL3PlanDate(item) || null;
          case 'subForL4ApprovDate':
            return SubmittedForL4PlanDate(item) || null;
          case 'subForL5ApprovDate':
            return SubmittedForL5PlanDate(item) || null;
          case 'latestActualDateAllLGates':
            return LLatestActualDate(item) || null;
          case 'latestMilestoneEndDate':
            return getLatestMilestoneEndDate(item) || null;
          case 'earliestMilestoneStartDate':
            return getEarliestMilestoneStartDate(item) || null;
          case 'cancellationDate':
            const historyEntry = item.history?.find(
              (entry) =>
                entry.action?.includes('Weekly Status was changed from') &&
                entry.action?.includes('to Cancelled')
            );
            return historyEntry?.timestamp ? new Date(historyEntry.timestamp) : null;
          default:
            return null;
        }
      };

      return dateSort(a, b, effectiveSortOrder, getVal);
    }

    // String-based Sorting for various fields
    if ([
      'l4DateComplianceCheck',
      'l3DateComplianceCheck',
      'netRecurringBenefitsRange',
      'Future implementation costs range',
      'planningSummary',
      'Planning summary (simplified)',
      'Stage',
      'Stage (simplified)',
      'Pipeline weekly variance (L0+)',
      'Pipeline weekly variance (L1+)',
      'Pipeline weekly variance (L2+)',
      'Pipeline weekly variance (L3+)',
      'Pipeline weekly variance (L4+)',
      'workstreamSponsorWorkstream',
      'workstreamLeadSubworkstream',
      'crossFunctionalSupportWorkstream',
      'Has qualitative KPI?',
      'frequencyAndDirectionBinned',
      'typeOfImpact',
      'whosePandL',
      'actualImpactUpdateCheck',
      '(FY24-25) Actual impact update check',
      '(FY23-24) Actual impact update check',
      'actualKpiUpdateCheck',
      'Workstream Lead pending approval?',
      'Workstream Sponsor pending approval?',
      'Finance lead pending approval?',
      'Description',
      'sector',
      'subsector',
      'type',
      'segment',
      'initiativeCategory',
      'size',
      'Strategy analyst pending approval?'
    ].includes(sortField)) {
      const getVal = (item) => {
        switch (sortField) {
          case 'l4DateComplianceCheck':
            return getComplianceStatus(item, 'L4');
          case 'l3DateComplianceCheck':
            return getComplianceStatus(item, 'L3');
          case 'netRecurringBenefitsRange':
            return getNetRecurringBenefitCategory(item);
          case 'Future implementation costs range':
            return getImplementationCostCatagory(item);
          case 'planningSummary':
            return InitiativePlanningSummary(item).planningStatus;
          case 'Planning summary (simplified)':
            return planningSummarySimplified(item);
          case 'Stage':
            return determineStage(item);
          case 'Stage (simplified)':
            return item.stage;
          case 'Description':
            return item.description;
          case 'Pipeline weekly variance (L0+)':
            return getNetRecurringBenefitChangeCategoryL0Plus(item);
          case 'Pipeline weekly variance (L1+)':
            return getNetRecurringBenefitChangeCategoryL1Plus(item);
          case 'Pipeline weekly variance (L2+)':
            return getNetRecurringBenefitChangeCategoryL2Plus(item);
          case 'Pipeline weekly variance (L3+)':
            return getNetRecurringBenefitChangeCategoryL3Plus(item);
          case 'Pipeline weekly variance (L4+)':
            return getNetRecurringBenefitChangeCategoryL4Plus(item);
          case 'workstreamSponsorWorkstream':
            return item.workstreamSponsorWorkstream?.[0]?.workstreamName;
          case 'workstreamLeadSubworkstream':
            return item.workstreamLeadSubworkstream?.[0]?.subworkstreamName;
          case 'crossFunctionalSupportWorkstream':
            return item.crossFunctionalSupportWorkstreamInfo?.[0]?.subworkstreamName;
          case 'Has qualitative KPI?':
            return getQualitativeKPI(item);
          case 'frequencyAndDirectionBinned':
            return getFrequencyAndDirection(item);
          case 'typeOfImpact':
            return getTypeOfImpact(item);
          case 'whosePandL':
            return getSubworkstreamCatagory(item, subworkstream);
          case 'sector':
            const sectorNames = [];
            if (item?.sectorAccounts && Array.isArray(item.sectorAccounts)) {
              item.sectorAccounts.forEach((sec) => {
                if (sec?.sector && subsectorData && Array.isArray(subsectorData)) {
                  const subsectorObj = subsectorData.find((sub) => sub._id === sec.sector);
                  if (subsectorObj && subsectorObj.sector?.sectorName) {
                    const nameValue = subsectorObj.sector.sectorName;
                    if (nameValue && !sectorNames.includes(nameValue)) {
                      sectorNames.push(nameValue);
                    }
                  }
                }
              });
            }
            return sectorNames.length > 0 ? sectorNames.join(', ') : '<not set>';
          case 'subsector':
            const subsectorNames = [];
            if (item?.sectorAccounts && Array.isArray(item.sectorAccounts)) {
              item.sectorAccounts.forEach((sec) => {
                if (sec?.sector && subsectorData && Array.isArray(subsectorData)) {
                  const subsectorObj = subsectorData.find((sub) => sub._id === sec.sector);
                  if (subsectorObj && subsectorObj.subsectorName) {
                    const nameValue = subsectorObj.subsectorName;
                    if (nameValue && !subsectorNames.includes(nameValue)) {
                      subsectorNames.push(nameValue);
                    }
                  }
                }
              });
            }
            return subsectorNames.length > 0 ? subsectorNames.join(', ') : '<not set>';
          case 'type':
            return item?.type || '';
          case 'segment':
            return item?.segment || '';
          case 'initiativeCategory':
            return item?.initiativeCategory || '';
          case 'size':
            return item?.size || '';
          case 'actualImpactUpdateCheck':
          case '(FY24-25) Actual impact update check':
          case '(FY23-24) Actual impact update check':
          case 'actualKpiUpdateCheck':
            return sortInitiativesByActuals(item, sortField);
          case 'Workstream Lead pending approval?':
            return checkWorkstreamLeadApprovalStatus(item);
          case 'Workstream Sponsor pending approval?':
            return checkWorkstreamSponsorApprovalStatus(item);
          case 'Finance lead pending approval?':
            return checkToFinanceApprovalStatus(item);
          case 'Strategy analyst pending approval?':
            return checkStrategyAnalystApprovalStatus(item);
          default:
            return '';
        }
      };
      return stringSort(a, b, effectiveSortOrder, getVal);
    }

    // Default string sorting for any other field
    let valueA, valueB;
    if (sortField.includes('.')) {
      const parts = sortField.split('.');
      valueA = parts.reduce((acc, part) => (acc?.[part] !== undefined ? acc[part] : undefined), a);
      valueB = parts.reduce((acc, part) => (acc?.[part] !== undefined ? acc[part] : undefined), b);
    } else {
      valueA = a[sortField];
      valueB = b[sortField];
    }

    const valA = String(valueA || '').toLowerCase();
    const valB = String(valueB || '').toLowerCase();
    return effectiveSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
  };

  // Sort the array and return it
  const sortedArray = [...filtered].sort(sorter);

  // Update the original array reference
  filtered.length = 0;
  filtered.push(...sortedArray);

  return filtered;
}

const getLatestHistoryEntry = (historyEntries, actionTerm, milestoneIdentifier) => {
  const matches = historyEntries
    .filter((entry) => entry.action?.includes(actionTerm(milestoneIdentifier)))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return matches[0] || null;
};

export function applyMilestoneSort(result, sortField, sortOrder, historyEntries, users) {
  if (!Array.isArray(result) || !sortField || sortField === 'None' || result.length === 0) {
    return;
  }

  const effectiveSortOrder = sortOrder || 'asc';

  // Pre-process history and user data to avoid redundant lookups in the sort function
  const historyCache = new Map();
  const userCache = new Map(users.map(user => [user._id, user]));

  const getUpdatedOnTimestamp = (milestoneNumber) => {
    const cacheKey = `updated-${milestoneNumber}`;
    if (!historyCache.has(cacheKey)) {
      const entry = getLatestHistoryEntry(
        historyEntries,
        (num) => `Milestone with Number ${num} has been updated.`,
        milestoneNumber
      );
      historyCache.set(cacheKey, entry?.timestamp ? new Date(entry.timestamp).getTime() : Infinity);
    }
    return historyCache.get(cacheKey);
  };

  const getCreatedOnTimestamp = (milestoneName) => {
    const cacheKey = `created-${milestoneName}`;
    if (!historyCache.has(cacheKey)) {
      const entry = getLatestHistoryEntry(
        historyEntries,
        (name) => `Milestone #${name} created`,
        milestoneName
      );
      historyCache.set(cacheKey, entry?.timestamp ? new Date(entry.timestamp).getTime() : Infinity);
    }
    return historyCache.get(cacheKey);
  };

  const getCreatorUpdaterName = (milestoneIdentifier, isCreatedBy) => {
    const cacheKey = isCreatedBy ? `creator-${milestoneIdentifier}` : `updater-${milestoneIdentifier}`;
    if (!historyCache.has(cacheKey)) {
      const actionTerm = isCreatedBy
        ? (id) => `Milestone #${id} created`
        : (id) => `Milestone with Number ${id} has been updated.`;

      const entry = getLatestHistoryEntry(historyEntries, actionTerm, milestoneIdentifier);
      const user = userCache.get(entry?.user);
      const name = user ? `${user.firstName} ${user.lastName}` : '';
      historyCache.set(cacheKey, name);
    }
    return historyCache.get(cacheKey);
  };

  const getOwnerName = (ownerId) => {
    const user = userCache.get(ownerId);
    return user ? `${user.firstName} ${user.lastName}` : '';
  };

  const getDaysDifference = (milestone) => {
    if (milestone.status === 'Completed') return 0;
    if (!milestone.endDate) return null;
    const timeDifference = new Date(milestone.endDate).getTime() - new Date().getTime();
    return Math.ceil(timeDifference / (1000 * 3600 * 24));
  };

  const getBin = (days) => {
    if (days === null) return '<not set>';
    if (days < 0) return 'Accelerated';
    if (days === 0) return 'Unchanged';
    if (days >= 1 && days <= 7) return 'Delayed (1 to 7 days)';
    if (days >= 8 && days <= 14) return 'Delayed (8 to 14 days)';
    if (days >= 15 && days <= 30) return 'Delayed (15 to 30 days)';
    if (days > 30) return 'Delayed (more than 30 days)';
    return 'Unknown';
  };

  // Define a stable sorting order for the binned column
  const binOrder = [
    '<not set>',
    'Accelerated',
    'Unchanged',
    'Delayed (1 to 7 days)',
    'Delayed (8 to 14 days)',
    'Delayed (15 to 30 days)',
    'Delayed (more than 30 days)'
  ];

  result.sort((a, b) => {
    let compareA, compareB;

    switch (sortField) {
      case 'Work starting date':
        compareA = a.startDate ? new Date(a.startDate).getTime() : Infinity;
        compareB = b.startDate ? new Date(b.startDate).getTime() : Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Milestone completion date':
        compareA = a.endDate ? new Date(a.endDate).getTime() : Infinity;
        compareB = b.endDate ? new Date(b.endDate).getTime() : Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Updated on':
        compareA = getUpdatedOnTimestamp(a.milestoneNumber);
        compareB = getUpdatedOnTimestamp(b.milestoneNumber);
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Milestone Created on':
        compareA = getCreatedOnTimestamp(a.name);
        compareB = getCreatedOnTimestamp(b.name);
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Created by':
        compareA = getCreatorUpdaterName(a.name, true);
        compareB = getCreatorUpdaterName(b.name, true);
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'Updated by':
        compareA = getCreatorUpdaterName(a.milestoneNumber, false);
        compareB = getCreatorUpdaterName(b.milestoneNumber, false);
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case '# - Name':
      case 'milestoneNumber':
        compareA = parseInt(a.milestoneNumber) || Infinity;
        compareB = parseInt(b.milestoneNumber) || Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Change in Milestone completion date (binned, current vs last week)':
        const binA = getBin(getDaysDifference(a));
        const binB = getBin(getDaysDifference(b));
        const indexA = binOrder.indexOf(binA);
        const indexB = binOrder.indexOf(binB);
        return effectiveSortOrder === 'asc' ? indexA - indexB : indexB - indexA;

      case 'Owner':
        compareA = getOwnerName(a.Owner);
        compareB = getOwnerName(b.Owner);
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'Planning summary (simplified)':
        compareA = getSimplifiedPlanningSummary(a);
        compareB = getSimplifiedPlanningSummary(b);
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      default:
        // Generic sorting for other fields
        compareA = a[sortField] || '';
        compareB = b[sortField] || '';
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);
    }
  });
}

const getLatestActivityEvent = (activity, actionItemNumber) => {
  const matches = activity
    .filter((event) => event.event?.includes(`Workstream action ${actionItemNumber} is updated`))
    .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));
  return matches[0] || null;
};

export function applyWorkstreamActionSort(actionResult, sortField, sortOrder) {
  if (!sortField || sortField === 'None' || !Array.isArray(actionResult) || actionResult.length === 0) {
    return;
  }

  const effectiveSortOrder = sortOrder || 'asc';

  actionResult.sort((a, b) => {
    let compareA, compareB;
    switch (sortField) {
      case 'workstreamActionName':
        compareA = a.workstreamActionName?.toLowerCase() || '';
        compareB = b.workstreamActionName?.toLowerCase() || '';
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'actionItemNumber':
        compareA = parseInt(a.actionItemNumber) || Infinity;
        compareB = parseInt(b.actionItemNumber) || Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'status':
        compareA = a.status?.toLowerCase() || '';
        compareB = b.status?.toLowerCase() || '';
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);
      case 'source':
        compareA = a?.source?.toLowerCase() || '';
        compareB = b?.source?.toLowerCase() || '';
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);
      case 'Action planning summary':
        compareA = determinePlanningSummary(a) || '';
        compareB = determinePlanningSummary(b) || '';
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'owner':
        const ownerNameA = a.owner ? `${a.owner.firstName} ${a.owner.lastName}`.toLowerCase() : '';
        const ownerNameB = b.owner ? `${b.owner.firstName} ${b.owner.lastName}`.toLowerCase() : '';
        compareA = ownerNameA;
        compareB = ownerNameB;
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'Accountable Workstream':
        const workstreamNameA =
          a.owner?.role?.name === 'Workstream Sponsor'
            ? a.owner?.workstream?.workstreamName?.toLowerCase() || ''
            : a.owner?.subworkstream?.workstream?.workstreamName?.toLowerCase() || '';

        const workstreamNameB =
          b.owner?.role?.name === 'Workstream Sponsor'
            ? b.owner?.workstream?.workstreamName?.toLowerCase() || ''
            : b.owner?.subworkstream?.workstream?.workstreamName?.toLowerCase() || '';

        compareA = workstreamNameA;
        compareB = workstreamNameB;
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'Accountable Subworkstream':
        const subworkstreamNameA = a.owner?.subworkstream?.subworkstreamName?.toLowerCase() || '';
        const subworkstreamNameB = b.owner?.subworkstream?.subworkstreamName?.toLowerCase() || '';
        compareA = subworkstreamNameA;
        compareB = subworkstreamNameB;
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      case 'Created on':
        compareA = a.created ? new Date(a.created).getTime() : Infinity;
        compareB = b.created ? new Date(b.created).getTime() : Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'Updated on':
        const updatedEventA = getLatestActivityEvent(a.activity || [], a.actionItemNumber);
        const updatedEventB = getLatestActivityEvent(b.activity || [], b.actionItemNumber);
        compareA = updatedEventA ? new Date(updatedEventA.eventDate).getTime() : Infinity;
        compareB = updatedEventB ? new Date(updatedEventB.eventDate).getTime() : Infinity;
        return effectiveSortOrder === 'asc' ? compareA - compareB : compareB - compareA;

      case 'actionUpdatedBy':
        const updatedOwnerA = a.activity?.find((event) =>
          event.event?.includes(`Workstream action ${a.actionItemNumber} is updated`)
        )?.owner;
        const updatedOwnerB = b.activity?.find((event) =>
          event.event?.includes(`Workstream action ${b.actionItemNumber} is updated`)
        )?.owner;

        compareA = updatedOwnerA
          ? `${updatedOwnerA.firstName || ''} ${updatedOwnerA.lastName || ''}`.trim().toLowerCase()
          : '';
        compareB = updatedOwnerB
          ? `${updatedOwnerB.firstName || ''} ${updatedOwnerB.lastName || ''}`.trim().toLowerCase()
          : '';

        return effectiveSortOrder === 'asc'
          ? compareA.localeCompare(compareB)
          : compareB.localeCompare(compareA);

      case 'createdBy':
        const createdByFullNameA = a.createdBy
          ? `${a.createdBy.firstName} ${a.createdBy.lastName}`.toLowerCase()
          : '';
        const createdByFullNameB = b.createdBy
          ? `${b.createdBy.firstName} ${b.createdBy.lastName}`.toLowerCase()
          : '';
        compareA = createdByFullNameA;
        compareB = createdByFullNameB;
        return effectiveSortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);

      default:
        // Generic sorting for other fields
        const valA = String(a[sortField] || '').toLowerCase();
        const valB = String(b[sortField] || '').toLowerCase();
        return effectiveSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
  });
}